import { useMemo, useState } from 'react';
import { useClientPaged } from '../hooks/useClientPaged';
import { TablePagination } from '../components/TablePagination';
import {
  Card, Select, Group, Button, Table, Loader, Center, Text, Modal, Stack, NumberInput,
  TextInput, Badge, SegmentedControl, Avatar, Tooltip, ActionIcon, Divider,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure, useDebouncedValue } from '@mantine/hooks';
import { IconSettings, IconPencilPlus, IconTrash, IconSearch, IconTrophy, IconMedal } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api, { fileUrl } from '../services/api';
import { useClasses } from '../hooks/useLookups';
import { useAuth } from '../contexts/AuthContext';
import { EmptyState } from '../components/EmptyState';
import { FetchOverlay } from '../components/FetchOverlay';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { formatDate } from '../lib/format';
import { confirmDelete } from '../lib/confirm';

/** Reyting sozlamalarini o'zgartira oladigan rollar (o'qituvchilar faqat jadvalni ko'radi). */
const MANAGER_ROLES = ['SUPER_ADMIN', 'DIRECTOR', 'ADMIN', 'ACCOUNTANT', 'RECEPTION'];

type Scope = 'class' | 'parallel' | 'school';

interface RankSetting {
  id: string;
  weightGrades: number;
  weightAttendance: number;
  weightBehavior: number;
  weightExams: number;
  weightHomework: number;
  periodDays: number;
  updatedAt: string;
}

interface Breakdown { score: number | null; count: number; avg: number | null }
interface AttBreakdown extends Breakdown { present: number; absent: number; late: number; excused: number }

interface RankRow {
  studentId: string;
  fullName: string;
  photo: string | null;
  classId: string;
  className: string;
  base: number;
  adjust: number;
  ball: number;
  avg: number;
  gradeCount: number;
  rank: number;
  breakdown: {
    grades: Breakdown;
    attendance: AttBreakdown;
    behavior: Breakdown;
    exams: Breakdown;
    homework: Breakdown;
  };
}

interface RankingResp {
  scope: string;
  scopeLabel: string;
  className: string | null;
  setting: RankSetting;
  period: { days: number; from: string };
  total: number;
  items: RankRow[];
}

interface Adjustment {
  id: string;
  studentId: string;
  delta: number;
  reason: string | null;
  createdAt: string;
  student?: { id: string; fullName: string; class?: { name: string } | null } | null;
  createdBy?: { id: string; fullName: string } | null;
}

/**
 * Sovrindorlar nishoni rangi (1-3 o'rin): oltin / kumush / bronza. Qolgan o'rinlarda
 * oddiy raqam chiqadi. Emoji ISHLATILMAYDI — u platformadan platformaga har xil
 * chiziladi va ba'zi Windows tizimlarida umuman ko'rinmaydi (loyiha qoidasi: ikonka).
 */
const MEDAL_COLOR: Record<number, string> = { 1: '#D4AF37', 2: '#A8A9AD', 3: '#B08D57' };

/** F.I.Sh. dan avatar uchun bosh harflar ("Ali Valiyev" → "AV"). */
function initials(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0] ?? '').join('').toUpperCase();
}

/**
 * Bitta yo'nalish (fanlar/davomat/xulq/testlar/uy vazifasi) katakchasi.
 * score === null → ma'lumot yo'q, 0 emas: chiziqcha ko'rsatiladi.
 */
function BreakdownCell({ b }: { b: Breakdown }) {
  const { t } = useTranslation();
  if (b.score === null) {
    return (
      <Tooltip label={t('rankingPage.tipNoData')} withArrow>
        <Text c="dimmed">—</Text>
      </Tooltip>
    );
  }
  return (
    <Tooltip
      label={`${t('rankingPage.tipCount', { count: b.count })} · ${t('rankingPage.tipAvg', { avg: b.avg?.toFixed(2) ?? '—' })}`}
      withArrow
    >
      <Text>{Math.round(b.score)}</Text>
    </Tooltip>
  );
}

export default function Ranking() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isManager = MANAGER_ROLES.includes(user?.role ?? '');

  const [classId, setClassId] = useState<string | null>(null);
  const [scope, setScope] = useState<Scope>('school');
  // Sinf tanlanmagan bo'lsa "sinf ichida"/"parallel" ma'nosiz — backend baribir butun maktabni qaytaradi
  const effScope: Scope = classId ? scope : 'school';
  // Reyting sahifalanmaydi (butun ro'yxat hisoblanadi) — qidiruv MIJOZ tomonda
  const [search, setSearch] = useState('');
  const [qDeb] = useDebouncedValue(search, 300);
  // Serverdan nechta o'rin olinadi (backend maksimumi — 500)
  const [limit, setLimit] = useState('200');
  // Davr tez tanlovi: bo'sh — global sozlamadagi hisob davri
  const [periodDays, setPeriodDays] = useState<string | null>(null);

  const [settingsOpened, { open: openSettingsModal, close: closeSettings }] = useDisclosure(false);
  const [adjustFor, setAdjustFor] = useState<RankRow | null>(null);
  const adjustStudentId = adjustFor?.studentId ?? '';

  const { data: classes } = useClasses();

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['ranking', classId, effScope, limit, periodDays],
    queryFn: async () =>
      (await api.get<RankingResp>('/ranking', {
        params: {
          classId: classId || undefined,
          scope: effScope,
          limit,
          periodDays: periodDays || undefined,
        },
      })).data,
    placeholderData: keepPreviousData,
  });

  // Sozlamalar modalini to'ldirish uchun (faqat rahbariyat ochadi)
  const { data: settingData } = useQuery({
    queryKey: ['ranking-settings'],
    queryFn: async () => (await api.get<RankSetting>('/ranking/settings')).data,
    enabled: isManager,
  });

  const { data: adjustments, isLoading: adjLoading } = useQuery({
    queryKey: ['ranking-adjustments', adjustStudentId],
    queryFn: async () =>
      (await api.get<Adjustment[]>('/ranking/adjustments', { params: { studentId: adjustStudentId } })).data,
    enabled: !!adjustStudentId,
  });

  /** Serverdan kelgan xatolik matni, bo'lmasa umumiy xabar. */
  const errMsg = (e: unknown) =>
    (e as { response?: { data?: { error?: string } } })?.response?.data?.error || t('rankingPage.saveError');
  const showError = (e: unknown) => notifications.show({ color: 'red', message: errMsg(e) });

  const settingsForm = useForm<{
    weightGrades: number | string; weightAttendance: number | string; weightBehavior: number | string;
    weightExams: number | string; weightHomework: number | string; periodDays: number | string;
  }>({
    initialValues: {
      weightGrades: 40, weightAttendance: 20, weightBehavior: 15,
      weightExams: 15, weightHomework: 10, periodDays: 30,
    },
  });

  const adjustForm = useForm<{ delta: number | string; reason: string }>({
    initialValues: { delta: '', reason: '' },
    validate: {
      delta: (v) => {
        const n = Number(v);
        return !Number.isInteger(n) || n === 0 || n < -100 || n > 100 ? t('rankingPage.deltaRequired') : null;
      },
    },
  });

  const sv = settingsForm.values;
  const weightSum =
    (Number(sv.weightGrades) || 0) + (Number(sv.weightAttendance) || 0) + (Number(sv.weightBehavior) || 0) +
    (Number(sv.weightExams) || 0) + (Number(sv.weightHomework) || 0);

  /** Modalni ochishdan OLDIN formani joriy sozlamalar bilan to'ldiramiz (render ichida emas — sikl bo'lmasin). */
  const openSettings = () => {
    const s = settingData ?? data?.setting;
    if (s) {
      settingsForm.setValues({
        weightGrades: s.weightGrades, weightAttendance: s.weightAttendance, weightBehavior: s.weightBehavior,
        weightExams: s.weightExams, weightHomework: s.weightHomework, periodDays: s.periodDays,
      });
    }
    settingsForm.clearErrors();
    openSettingsModal();
  };

  const openAdjust = (r: RankRow) => {
    adjustForm.setValues({ delta: '', reason: '' });
    adjustForm.clearErrors();
    setAdjustFor(r);
  };

  const saveSettingsMut = useMutation({
    mutationFn: (v: typeof settingsForm.values) =>
      api.put('/ranking/settings', {
        weightGrades: Number(v.weightGrades) || 0,
        weightAttendance: Number(v.weightAttendance) || 0,
        weightBehavior: Number(v.weightBehavior) || 0,
        weightExams: Number(v.weightExams) || 0,
        weightHomework: Number(v.weightHomework) || 0,
        periodDays: Number(v.periodDays) || 1,
      }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('rankingPage.settingsSaved') });
      qc.invalidateQueries({ queryKey: ['ranking'] });
      qc.invalidateQueries({ queryKey: ['ranking-settings'] });
      closeSettings();
    },
    onError: showError,
  });

  const addAdjustMut = useMutation({
    mutationFn: (v: typeof adjustForm.values) =>
      api.post('/ranking/adjustments', {
        studentId: adjustStudentId,
        delta: Number(v.delta),
        reason: v.reason.trim() || undefined,
      }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('rankingPage.adjustAdded') });
      qc.invalidateQueries({ queryKey: ['ranking'] });
      qc.invalidateQueries({ queryKey: ['ranking-adjustments', adjustStudentId] });
      adjustForm.reset(); // modal ochiq qoladi — ro'yxat darhol yangilanadi
    },
    onError: showError,
  });

  const delAdjustMut = useMutation({
    mutationFn: (id: string) => api.delete(`/ranking/adjustments/${id}`),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('rankingPage.adjustDeleted') });
      qc.invalidateQueries({ queryKey: ['ranking'] });
      qc.invalidateQueries({ queryKey: ['ranking-adjustments', adjustStudentId] });
    },
    onError: showError,
  });

  // Serverdan kelgan (limit bilan kesilgan) ro'yxat — `rank` maydoni shu yerda TAYYOR.
  const items = data?.items ?? [];
  // Qidiruv faqat ko'rinishni filtrlaydi: o'rin raqami ASL ro'yxatnikidan olinadi,
  // shuning uchun filtrdan keyin qayta raqamlanmaydi.
  const visible = useMemo(() => {
    const q = qDeb.trim().toLowerCase();
    return q ? items.filter((r) => r.fullName.toLowerCase().includes(q)) : items;
  }, [items, qDeb]);

  /* Reyting serverda BUTUNLAY hisoblanadi (o'rin raqami hamma o'quvchi bo'yicha),
     shuning uchun uni serverda bo'lib bo'lmaydi. Lekin 500+ qatorni DOM ga
     chizish sahifani sekinlashtiradi va kerakli o'quvchini topish qiyin —
     ko'rsatishni brauzerda bo'lamiz. */
  const sahifa = useClientPaged(visible, 'ranking');

  return (
    <>
      <PageHeader
        title={t('pages.ranking.title')}
        subtitle={data ? t('rankingPage.periodInfo', { days: data.period.days }) : undefined}
        action={
          isManager ? (
            <Button leftSection={<IconSettings size={18} />} onClick={openSettings}>
              {t('rankingPage.settings')}
            </Button>
          ) : undefined
        }
      />

      <Card mb="md">
        <Group align="flex-end">
          <Select
            label={t('fields.class')}
            placeholder={t('common.selectPlaceholder')}
            clearable
            searchable
            w={220}
            data={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
            value={classId}
            onChange={setClassId}
          />
          <SegmentedControl
            value={effScope}
            onChange={(v) => setScope(v as Scope)}
            data={[
              { value: 'class', label: t('rankingPage.scopeClass'), disabled: !classId },
              { value: 'parallel', label: t('rankingPage.scopeParallel'), disabled: !classId },
              { value: 'school', label: t('rankingPage.scopeSchool') },
            ]}
          />
          <Select
            placeholder={t('rankingPage.periodDays')}
            data={[
              { value: '7', label: t('rankingPage.period7') },
              { value: '30', label: t('rankingPage.period30') },
              { value: '90', label: t('rankingPage.period90') },
            ]}
            value={periodDays}
            onChange={setPeriodDays}
            clearable
            w={180}
          />
          <TextInput
            placeholder={t('rankingPage.search')}
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={260}
          />
        </Group>

        {!classId && (
          <Stack gap={4} mt="xs">
            <Text c="dimmed" fz="sm">{t('rankingPage.schoolHint')}</Text>
            {scope !== 'school' && <Text c="dimmed" fz="sm">{t('rankingPage.scopeNeedsClass')}</Text>}
          </Stack>
        )}

        {data && (
          <Stack gap={4} mt="xs">
            <Text c="dimmed" fz="sm">
              {t('rankingPage.weightsInfo', {
                grades: data.setting.weightGrades,
                attendance: data.setting.weightAttendance,
                behavior: data.setting.weightBehavior,
                exams: data.setting.weightExams,
                homework: data.setting.weightHomework,
              })}
            </Text>
            <Text c="dimmed" fz="sm">{t('rankingPage.totalStudents', { count: data.total })}</Text>
          </Stack>
        )}
      </Card>

      {/* Server ro'yxatni `limit` bilan kesadi — jimgina emas, ochiq aytamiz.
          Limit 500 ga ko'tarilgach kesim yo'qoladi, lekin tanlov qaytarib olinishi
          uchun ko'rinib turadi. */}
      {!!items.length && data && (data.total > items.length || limit !== '200') && (
        <Group justify="space-between" align="flex-end" mb="xs" gap="sm" wrap="wrap">
          <Text size="sm" c="dimmed">
            {t('rankingPage.shownOf', { shown: items.length, total: data.total })}
          </Text>
          <Select
            aria-label={t('rankingPage.showLimit')}
            data={[
              { value: '200', label: '200' },
              { value: '500', label: '500' },
            ]}
            value={limit}
            onChange={(v) => v && setLimit(v)}
            allowDeselect={false}
            w={120}
          />
        </Group>
      )}
      {/* Karta doim ko'rinadi — yuklanish/bo'sh holat ham sarlavha ostida almashadi */}
      <SectionCard title={t('common.list')} icon={IconTrophy} p={0} pos="relative">
        <FetchOverlay visible={isFetching} />
        {isLoading ? (
          <Center h={160}><Loader /></Center>
        ) : !items.length ? (
          <EmptyState icon={IconTrophy} title={t('rankingPage.empty')} height={180} />
        ) : !visible.length ? (
          <EmptyState icon={IconSearch} title={t('common.noMatch')} height={180} />
        ) : (
          <>
          <Table.ScrollContainer minWidth={900}>
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={60}>{t('rankingPage.colRank')}</Table.Th>
                  <Table.Th>{t('rankingPage.colStudent')}</Table.Th>
                  <Table.Th>{t('rankingPage.colClass')}</Table.Th>
                  <Table.Th>{t('rankingPage.colBall')}</Table.Th>
                  <Table.Th>{t('rankingPage.colGrades')}</Table.Th>
                  <Table.Th>{t('rankingPage.colAttendance')}</Table.Th>
                  <Table.Th>{t('rankingPage.colBehavior')}</Table.Th>
                  <Table.Th>{t('rankingPage.colExams')}</Table.Th>
                  <Table.Th>{t('rankingPage.colHomework')}</Table.Th>
                  <Table.Th>{t('rankingPage.colAdjust')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {sahifa.items.map((r) => (
                  <Table.Tr key={r.studentId}>
                    <Table.Td>
                      {MEDAL_COLOR[r.rank]
                        ? <IconMedal size={22} stroke={1.8} color={MEDAL_COLOR[r.rank]} title={String(r.rank)} />
                        : <Text fw={500}>{r.rank}</Text>}
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <Avatar src={fileUrl(r.photo)} radius="xl" size="sm" color="brand" alt={r.fullName}>
                          {initials(r.fullName)}
                        </Avatar>
                        <Text fw={500}>{r.fullName}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>{r.className}</Table.Td>
                    <Table.Td>
                      <Badge size="lg" color={r.ball >= 80 ? 'teal' : r.ball >= 50 ? 'yellow' : 'red'}>
                        {Math.round(r.ball)}
                      </Badge>
                    </Table.Td>
                    <Table.Td><BreakdownCell b={r.breakdown.grades} /></Table.Td>
                    <Table.Td><BreakdownCell b={r.breakdown.attendance} /></Table.Td>
                    <Table.Td><BreakdownCell b={r.breakdown.behavior} /></Table.Td>
                    <Table.Td><BreakdownCell b={r.breakdown.exams} /></Table.Td>
                    <Table.Td><BreakdownCell b={r.breakdown.homework} /></Table.Td>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        {r.adjust !== 0 && (
                          <Badge color={r.adjust > 0 ? 'teal' : 'red'} variant="light">
                            {r.adjust > 0 ? `+${r.adjust}` : r.adjust}
                          </Badge>
                        )}
                        {isManager && (
                          <ActionIcon variant="subtle" onClick={() => openAdjust(r)} aria-label={t('rankingPage.adjust')}>
                            <IconPencilPlus size={16} />
                          </ActionIcon>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <TablePagination
            page={sahifa.page}
            pages={sahifa.pages}
            total={sahifa.total}
            limit={sahifa.limit}
            onPage={sahifa.setPage}
            onLimit={sahifa.setLimit}
          />
          </>
        )}
      </SectionCard>

      {isManager && (
        <Modal opened={settingsOpened} onClose={closeSettings} title={t('rankingPage.settingsTitle')} centered>
          <form onSubmit={settingsForm.onSubmit((v) => saveSettingsMut.mutate(v))}>
            <Stack>
              <NumberInput label={t('rankingPage.weightGrades')} min={0} max={100} {...settingsForm.getInputProps('weightGrades')} />
              <NumberInput label={t('rankingPage.weightAttendance')} min={0} max={100} {...settingsForm.getInputProps('weightAttendance')} />
              <NumberInput label={t('rankingPage.weightBehavior')} min={0} max={100} {...settingsForm.getInputProps('weightBehavior')} />
              <NumberInput label={t('rankingPage.weightExams')} min={0} max={100} {...settingsForm.getInputProps('weightExams')} />
              <NumberInput label={t('rankingPage.weightHomework')} min={0} max={100} {...settingsForm.getInputProps('weightHomework')} />
              <Stack gap={2}>
                <Text fz="sm" c="dimmed">{t('rankingPage.weightsSum', { sum: weightSum })}</Text>
                <Text fz="sm" c="dimmed">{t('rankingPage.normalizeHint')}</Text>
              </Stack>
              <NumberInput label={t('rankingPage.periodDays')} min={1} max={365} {...settingsForm.getInputProps('periodDays')} />
              <Group justify="flex-end" mt="sm">
                <Button variant="default" onClick={closeSettings}>{t('common.cancel')}</Button>
                <Button type="submit" loading={saveSettingsMut.isPending}>{t('common.save')}</Button>
              </Group>
            </Stack>
          </form>
        </Modal>
      )}

      {isManager && (
        <Modal opened={!!adjustFor} onClose={() => setAdjustFor(null)} title={t('rankingPage.adjustTitle')} centered>
          <Stack>
            <Group gap="xs" wrap="nowrap">
              <Avatar src={fileUrl(adjustFor?.photo)} radius="xl" size="sm" color="brand">
                {adjustFor ? initials(adjustFor.fullName) : ''}
              </Avatar>
              <div>
                <Text fw={600}>{adjustFor?.fullName}</Text>
                <Text fz={13} lh={1.4} c="dimmed">{adjustFor?.className}</Text>
              </div>
            </Group>

            <form onSubmit={adjustForm.onSubmit((v) => addAdjustMut.mutate(v))}>
              <Stack>
                <NumberInput
                  label={t('rankingPage.delta')}
                  description={t('rankingPage.deltaHint')}
                  min={-100}
                  max={100}
                  allowNegative
                  {...adjustForm.getInputProps('delta')}
                />
                <TextInput
                  label={t('rankingPage.reason')}
                  placeholder={t('rankingPage.reasonPlaceholder')}
                  {...adjustForm.getInputProps('reason')}
                />
                <Group justify="flex-end">
                  <Button type="submit" loading={addAdjustMut.isPending}>{t('common.save')}</Button>
                </Group>
              </Stack>
            </form>

            <Divider />

            <Text fw={600} fz="sm">{t('rankingPage.adjustList')}</Text>
            {adjLoading ? (
              <Center h={60}><Loader size="sm" /></Center>
            ) : !adjustments?.length ? (
              <Text c="dimmed" fz="sm">{t('rankingPage.noAdjust')}</Text>
            ) : (
              <Stack gap="xs">
                {adjustments.map((a) => (
                  <Group key={a.id} justify="space-between" wrap="nowrap">
                    <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                      <Badge color={a.delta > 0 ? 'teal' : 'red'} variant="light">
                        {a.delta > 0 ? `+${a.delta}` : a.delta}
                      </Badge>
                      <div style={{ minWidth: 0 }}>
                        <Text fz="sm" lineClamp={1}>{a.reason || '—'}</Text>
                        <Text fz={12} lh={1.4} c="dimmed">
                          {formatDate(a.createdAt)} · {a.createdBy?.fullName ?? '—'}
                        </Text>
                      </div>
                    </Group>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      aria-label={t('common.delete')}
                      onClick={() => confirmDelete(a.reason || String(a.delta), () => delAdjustMut.mutate(a.id))}
                     loading={delAdjustMut.isPending}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                ))}
              </Stack>
            )}
          </Stack>
        </Modal>
      )}
    </>
  );
}
